import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ClipboardCheck, Plus, X, Pencil, Trash2, Loader2, Upload, Download, Users, Ticket, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';
import {
  getEvents, createEvent, updateEvent, deleteEvent, uploadMemberPhoto,
  getRsvpSummary, getRsvps, deleteRsvp, downloadCsv, setRsvpPaid,
} from '../../lib/api';
import { fmtTimeRange, money, priceSummary } from '../../lib/events';
import { EventPricing, DEFAULT_PAYMENT } from './EventPricing';
import { IMAGES } from '../../mock';

const EMPTY = { title: '', date: new Date().toISOString().slice(0, 10), start_time: '', end_time: '', location: '', description: '', image: '', published: true, ticket_types: [], promo_codes: [], payment: { ...DEFAULT_PAYMENT } };
const inputCls = 'w-full px-3 py-2.5 bg-white border border-neutral-200 rounded-sm font-sans-ah text-[14px] focus:outline-none focus:border-[#4a9cd4]';
const lbl = 'font-sans-ah text-[11px] tracking-wide-ah text-[#777] uppercase block mb-1';

function EventForm({ value, onChange, onSave, onCancel }) {
  const [uploading, setUploading] = useState(false);
  const f = (k) => (e) => onChange({ ...value, [k]: e.target.value });
  const upload = async (e) => {
    const file = e.target.files?.[0]; e.target.value = '';
    if (!file) return;
    setUploading(true);
    try { const r = await uploadMemberPhoto(file); onChange({ ...value, image: r.absoluteUrl }); }
    catch (err) { toast.error(err?.response?.data?.detail || 'Upload failed'); }
    setUploading(false);
  };
  return (
    <form onSubmit={onSave} data-testid="event-form" className="bg-[#f8f6f2] rounded-sm p-6 mb-8 border border-neutral-200">
      <div className="flex justify-between items-center mb-4">
        <h3 className="font-serif-ah text-[20px]">{value.id ? 'Edit' : 'New'} Event</h3>
        <button type="button" onClick={onCancel} aria-label="Close"><X size={20} className="text-[#888]" /></button>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <div className="md:col-span-2"><label htmlFor="ev-title" className={lbl}>Title *</label><input id="ev-title" data-testid="event-title-input" className={inputCls} value={value.title} onChange={f('title')} /></div>
        <div><label htmlFor="ev-date" className={lbl}>Date *</label><input id="ev-date" type="date" data-testid="event-date-input" className={inputCls} value={value.date} onChange={f('date')} /></div>
        <div><label htmlFor="ev-loc" className={lbl}>Location</label><input id="ev-loc" data-testid="event-location-input" className={inputCls} value={value.location} onChange={f('location')} /></div>
        <div><label htmlFor="ev-start" className={lbl}>Start time</label><input id="ev-start" type="time" data-testid="event-start-input" className={inputCls} value={value.start_time} onChange={f('start_time')} /></div>
        <div><label htmlFor="ev-end" className={lbl}>End time</label><input id="ev-end" type="time" data-testid="event-end-input" className={inputCls} value={value.end_time} onChange={f('end_time')} /></div>
      </div>
      <label htmlFor="ev-desc" className={lbl + ' mt-4'}>Description</label>
      <textarea id="ev-desc" rows={5} data-testid="event-description-input" className={inputCls} value={value.description} onChange={f('description')} />
      <p className={lbl + ' mt-4'}>Photo</p>
      <div className="flex items-center gap-3">
        {value.image && <div className="w-20 h-14 rounded-sm bg-cover bg-center" style={{ backgroundImage: `url(${value.image})` }} />}
        <label data-testid="event-photo-upload" className="cursor-pointer font-sans-ah text-[12px] px-4 py-2.5 rounded-sm border border-neutral-300 text-[#555] hover:bg-white flex items-center gap-2">
          {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} UPLOAD PHOTO
          <input type="file" accept="image/*" className="hidden" onChange={upload} />
        </label>
        {value.image && <button type="button" onClick={() => onChange({ ...value, image: '' })} className="font-sans-ah text-[12px] text-red-500">Remove</button>}
      </div>
      <label className="flex items-center gap-2 mt-4 font-sans-ah text-[14px]">
        <input type="checkbox" data-testid="event-published-checkbox" checked={value.published} onChange={(e) => onChange({ ...value, published: e.target.checked })} /> Published
      </label>
      <EventPricing value={value} onChange={onChange} />
      <button type="submit" data-testid="event-save-btn" className="btn-ah-solid font-sans-ah text-[12px] px-6 py-3 rounded-sm mt-5">SAVE EVENT</button>
    </form>
  );
}

export function EventsManager() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = useCallback(() => getEvents(true).then(setItems).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const save = async (e) => {
    e.preventDefault();
    if (!editing.title.trim() || !editing.date) { toast.error('Title and date are required'); return; }
    const types = editing.ticket_types || [];
    if (types.some((t) => !t.name.trim() || t.price === '' || t.price < 0)) { toast.error('Each ticket type needs a name and a price (0 for free)'); return; }
    const codes = editing.promo_codes || [];
    if (codes.some((c) => c.code.length < 2 || !(c.value > 0))) { toast.error('Each promo code needs a code and a value'); return; }
    if (new Set(codes.map((c) => c.code)).size !== codes.length) { toast.error('Promo codes must be unique'); return; }
    const pay = { ...DEFAULT_PAYMENT, ...(editing.payment || {}) };
    if (pay.online && !/^https?:\/\//i.test(pay.online_link)) { toast.error('Please enter a valid online payment link (https://...)'); return; }
    if (types.some((t) => t.price > 0) && !pay.pay_at_door && !pay.etransfer && !pay.online) { toast.error('Choose at least one payment option'); return; }
    const data = { ...editing, ticket_types: types.map((t) => ({ ...t, price: Number(t.price) || 0 })), payment: pay };
    try {
      if (editing.id) await updateEvent(editing.id, data); else await createEvent(data);
      toast.success('Event saved'); setEditing(null); load();
    } catch { toast.error('Save failed'); }
  };
  const remove = async (id) => {
    if (!window.confirm('Delete this event and all of its RSVPs?')) return;
    await deleteEvent(id); toast.success('Deleted'); load();
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="font-serif-ah text-[26px] text-[#2f2f2f]">Manage Events</h2>
        <button onClick={() => setEditing({ ...EMPTY })} data-testid="new-event-btn" className="btn-ah-solid font-sans-ah text-[12px] px-5 py-3 rounded-sm flex items-center gap-2"><Plus size={16} /> NEW EVENT</button>
      </div>
      {editing && <EventForm value={editing} onChange={setEditing} onSave={save} onCancel={() => setEditing(null)} />}
      {items.length === 0 && <p className="font-sans-ah text-[#888]">No events yet.</p>}
      <div className="space-y-3">
        {items.map((ev) => (
          <div key={ev.id} data-testid={`admin-event-${ev.id}`} className="flex items-center gap-4 bg-white border border-neutral-100 rounded-sm p-4">
            <div className="w-16 h-16 rounded-sm bg-cover bg-center bg-[#eef4f8] shrink-0" style={{ backgroundImage: `url(${ev.image || IMAGES.heroA})` }} />
            <div className="flex-1 min-w-0">
              <p className="font-serif-ah text-[18px] text-[#2f2f2f] truncate">{ev.title}</p>
              <p className="font-sans-ah text-[12px] text-[#999]">{ev.date}{fmtTimeRange(ev) ? ` • ${fmtTimeRange(ev)}` : ''}{ev.location ? ` • ${ev.location}` : ''} • {priceSummary(ev)} • {ev.published ? 'Published' : 'Draft'}</p>
            </div>
            <button onClick={() => setEditing({ ticket_types: [], promo_codes: [], ...ev, payment: { ...DEFAULT_PAYMENT, ...(ev.payment || {}) } })} aria-label="Edit event" data-testid={`edit-event-${ev.id}`} className="p-2 text-[#4a9cd4] hover:bg-[#eef4f8] rounded-sm"><Pencil size={17} /></button>
            <button onClick={() => remove(ev.id)} aria-label="Delete event" data-testid={`delete-event-${ev.id}`} className="p-2 text-red-500 hover:bg-red-50 rounded-sm"><Trash2 size={17} /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

const PAY = { door: 'At the door', etransfer: 'E-transfer', online: 'Online' };

function GuestList({ eventId, onChange }) {
  const [rows, setRows] = useState(null);
  const load = useCallback(() => getRsvps(eventId).then(setRows).catch(() => setRows([])), [eventId]);
  useEffect(() => { load(); }, [load]);
  const remove = async (id) => {
    if (!window.confirm('Remove this RSVP?')) return;
    await deleteRsvp(id); toast.success('RSVP removed'); load(); onChange();
  };
  const togglePaid = async (r) => {
    try { await setRsvpPaid(r.id, !r.paid); setRows((rs) => rs.map((x) => (x.id === r.id ? { ...x, paid: !r.paid } : x))); onChange(); } catch { toast.error('Could not update payment'); }
  };
  const paidCols = (rows || []).some((r) => r.items?.length);
  if (!rows) return <Loader2 size={18} className="animate-spin text-[#4a9cd4] m-4" />;
  if (!rows.length) return <p className="font-sans-ah text-[13px] text-[#999] p-4">No RSVPs yet.</p>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full font-sans-ah text-[13px]" data-testid={`guest-list-${eventId}`}>
        <thead><tr className="text-left text-[#999] text-[11px] tracking-wide-ah uppercase">
          <th className="p-3">Name</th><th className="p-3">Email</th><th className="p-3">Phone</th><th className="p-3">Tickets</th>{paidCols && <><th className="p-3">Total</th><th className="p-3">Payment</th><th className="p-3">Paid</th></>}<th className="p-3">Date</th><th className="p-3" />
        </tr></thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} className="border-t border-neutral-100" data-testid={`rsvp-row-${r.id}`}>
              <td className="p-3 text-[#2f2f2f]">{r.full_name}{r.business_name && <span className="block text-[11px] text-[#4a9cd4]" data-testid={`rsvp-business-${r.id}`}>Member: {r.business_name}</span>}
                {r.guests?.length > 0 && <span className="block text-[11px] text-[#888]" data-testid={`rsvp-guests-${r.id}`}>Guests: {r.guests.map((g) => g.name).join(', ')}</span>}</td>
              <td className="p-3"><a href={`mailto:${r.email}`} className="text-[#4a9cd4]">{r.email}</a></td>
              <td className="p-3">{r.phone}</td>
              <td className="p-3"><span className="font-medium">{r.tickets}</span>
                {r.items?.length > 0 && <span className="block text-[11px] text-[#999]">{r.items.map((x) => `${x.qty}× ${x.name}`).join(', ')}</span>}</td>
              {paidCols && (
                <>
                  <td className="p-3" data-testid={`rsvp-total-${r.id}`}>{money(r.total)}{r.promo_code && <span className="block text-[11px] text-green-700">{r.promo_code} −{money(r.discount)}</span>}</td>
                  <td className="p-3 text-[12px]">{r.total ? PAY[r.payment_method] || '—' : '—'}</td>
                  <td className="p-3">{r.total ? (
                    <button onClick={() => togglePaid(r)} data-testid={`rsvp-paid-toggle-${r.id}`}
                      className={`font-sans-ah text-[11px] tracking-wide-ah px-2.5 py-1 rounded-full border ${r.paid ? 'bg-green-600 border-green-600 text-white' : 'border-amber-500 text-amber-600 hover:bg-amber-50'}`}>{r.paid ? 'PAID' : 'UNPAID'}</button>
                  ) : <span className="text-[11px] text-[#999]">Free</span>}</td>
                </>
              )}
              <td className="p-3 text-[#999]">{(r.created_at || '').slice(0, 10)}</td>
              <td className="p-3 text-right"><button onClick={() => remove(r.id)} aria-label="Remove RSVP" data-testid={`rsvp-delete-${r.id}`} className="p-1.5 text-red-500 hover:bg-red-50 rounded-sm"><Trash2 size={15} /></button></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function RsvpsManager() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(null);
  const load = useCallback(() => getRsvpSummary().then(setItems).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const totals = items.reduce((a, i) => ({ rsvps: a.rsvps + i.rsvps, tickets: a.tickets + i.tickets }), { rsvps: 0, tickets: 0 });

  return (
    <div>
      <h2 className="font-serif-ah text-[26px] text-[#2f2f2f] mb-1">Event RSVPs</h2>
      <p className="font-sans-ah text-[13px] text-[#888] mb-6" data-testid="rsvp-totals">{totals.rsvps} RSVP{totals.rsvps === 1 ? '' : 's'} • {totals.tickets} ticket{totals.tickets === 1 ? '' : 's'} across all events</p>
      {items.length === 0 && <p className="font-sans-ah text-[#888]">No events yet. Create one in the Events tab, or tag a News post as "EVENT".</p>}
      <div className="space-y-3">
        {items.map((ev) => (
          <div key={ev.id} className="bg-white border border-neutral-100 rounded-sm" data-testid={`rsvp-event-${ev.id}`}>
            <div className="flex flex-wrap items-center gap-4 p-4">
              <button onClick={() => setOpen(open === ev.id ? null : ev.id)} data-testid={`rsvp-toggle-${ev.id}`} className="flex-1 min-w-[200px] flex items-center gap-3 text-left">
                <ChevronDown size={18} className={`text-[#4a9cd4] transition-transform ${open === ev.id ? 'rotate-180' : ''}`} />
                <div>
                  <p className="font-serif-ah text-[18px] text-[#2f2f2f]">{ev.title}</p>
                  <p className="font-sans-ah text-[12px] text-[#999]">{ev.date} • {ev.source === 'news' ? 'News post' : 'Event'}</p>
                </div>
              </button>
              <span className="flex items-center gap-1.5 font-sans-ah text-[13px] text-[#555]"><Users size={15} className="text-[#4a9cd4]" /> {ev.rsvps} RSVP{ev.rsvps === 1 ? '' : 's'}</span>
              <span className="flex items-center gap-1.5 font-sans-ah text-[13px] text-[#555]" data-testid={`rsvp-tickets-${ev.id}`}><Ticket size={15} className="text-[#4a9cd4]" /> {ev.tickets} ticket{ev.tickets === 1 ? '' : 's'}</span>
              {ev.revenue > 0 && (
                <span className="font-sans-ah text-[13px] text-[#555]" data-testid={`rsvp-revenue-${ev.id}`}>
                  <strong className="text-green-700">${(ev.collected || 0).toFixed(2).replace(/\.00$/, '')}</strong> collected{ev.revenue > ev.collected ? <> • <strong className="text-amber-600">{money(ev.revenue - ev.collected)}</strong> owing</> : null}
                </span>
              )}
              <Link to={`/admin/checkin/${ev.id}`} data-testid={`rsvp-checkin-${ev.id}`}
                className="font-sans-ah text-[12px] tracking-wide-ah px-4 py-2.5 rounded-sm bg-[#1f2c33] text-white hover:bg-[#2c3e48] flex items-center gap-2">
                <ClipboardCheck size={14} /> CHECK-IN
              </Link>
              <button onClick={() => downloadCsv('rsvps', { event_id: ev.id })} disabled={!ev.rsvps} data-testid={`rsvp-export-${ev.id}`}
                className="font-sans-ah text-[12px] tracking-wide-ah px-4 py-2.5 rounded-sm border border-neutral-300 text-[#555] hover:bg-[#f8f6f2] flex items-center gap-2 disabled:opacity-40">
                <Download size={14} /> CSV
              </button>
            </div>
            {open === ev.id && <div className="border-t border-neutral-100"><GuestList eventId={ev.id} onChange={load} /></div>}
          </div>
        ))}
      </div>
    </div>
  );
}
